import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ChevronRightRounded } from '@mui/icons-material';
import apis from '@src/apis';
import actions from '@src/redux/actions';
import Typography from '@src/new-components/Typography';
import {
  FAVOURITE_VOICES_ACTION,
  FAVOURITE_VOICES_LIMIT,
} from '@src/constants/voiceLibrary';
import { VOICE_CATEGORY } from '@src/constants/voiceCloning';
import { MOE_EVENTS } from '@src/constants/moengage';
import { trackEvent } from '@src/services/moengage';
import ROUTES from '@src/constants/route';
import { COLOR } from '@src/styles/color';
import fireIcon from '@src/assets/icons/fireIcon.svg';
import VoiceItem from './VoiceItem';
import VoiceItemSkeleton from './VoiceItemSkeleton';
import useTeachingVoices from './useTeachingVoices';
import { StyledTeachingVoices } from './index.style';

// The flag decides how many voices actually arrive, but the placeholders have
// to be laid out before it is read — six is the full grid the design sizes for.
const SKELETON_KEYS = Array.from(
  { length: 6 },
  (_, index) => `voice-skeleton-${index}`,
);

const TeachingVoices = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const { voices, favouriteCodes, setFavouriteCodes, loading } =
    useTeachingVoices();
  const [playingVoiceId, setPlayingVoiceId] = useState(null);

  // One element for the whole section: playing a voice replaces the source, so
  // two previews can never overlap.
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = new Audio();

  useEffect(() => {
    const audio = audioRef.current;
    const handleEnded = () => setPlayingVoiceId(null);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.pause();
    };
  }, []);

  const handlePlay = (voice) => {
    const audio = audioRef.current;

    if (playingVoiceId === voice.id) {
      audio.pause();
      setPlayingVoiceId(null);
      return;
    }
    if (!voice.demo) return;

    audio.src = voice.demo;
    // Swapping the source mid-play aborts the previous promise, which would
    // otherwise surface as an unhandled rejection.
    audio.play().catch(() => setPlayingVoiceId(null));
    setPlayingVoiceId(voice.id);
    trackEvent(MOE_EVENTS.EDUCATORS_VOICE_PREVIEWED, {
      voice_code: voice.code,
    });
  };

  const handleFavourite = async (voice) => {
    const wasFavourite = favouriteCodes.includes(voice.code);

    if (!wasFavourite && favouriteCodes.length >= FAVOURITE_VOICES_LIMIT) {
      dispatch(
        actions.noti.push({
          severity: 'warning',
          message: t('maxFavouriteVoices', {
            numFavouriteVoices: FAVOURITE_VOICES_LIMIT,
          }),
        }),
      );
      return;
    }

    // Flip first so the heart answers straight away, then undo if the
    // request fails.
    setFavouriteCodes((codes) =>
      wasFavourite
        ? codes.filter((code) => code !== voice.code)
        : [...codes, voice.code],
    );

    try {
      // The api layer rejects on failure, so a throw is the only signal that
      // this did not work — the success body carries no status flag.
      await apis.favouriteVoices.updateFavouriteVoices({
        voiceCode: voice.code,
        action: wasFavourite
          ? FAVOURITE_VOICES_ACTION.REMOVE
          : FAVOURITE_VOICES_ACTION.ADD,
      });
    } catch {
      setFavouriteCodes((codes) =>
        wasFavourite
          ? [...codes, voice.code]
          : codes.filter((code) => code !== voice.code),
      );
    }
  };

  // The library has no field filter of its own to read from, so hand the
  // choice over in the router state — Content seeds its filter from it.
  const handleSeeMore = () =>
    history.push(ROUTES.VOICE_LIBRARY, {
      categories: [VOICE_CATEGORY.EDUCATION],
    });

  // Nothing to show once the voices are in: the flag is off, misconfigured, or
  // the request failed. Better an absent section than an empty one.
  if (!loading && !voices.length) return null;

  return (
    <StyledTeachingVoices>
      <div className="voices-header">
        <img src={fireIcon} alt="" className="voices-title-icon" />
        <Typography variant="body-24-semibold" color={COLOR.label2[100]}>
          {t('educatorsTeachingVoices')}
        </Typography>
        <div
          className="voices-see-more"
          role="presentation"
          onClick={handleSeeMore}
        >
          <Typography variant="body-14-regular" color={COLOR.label2[64]}>
            {t('viewMore')}
          </Typography>
          <ChevronRightRounded className="voices-see-more-icon" />
        </div>
      </div>

      <div className="voices-grid">
        {loading
          ? SKELETON_KEYS.map((key) => <VoiceItemSkeleton key={key} />)
          : voices.map((voice) => (
              <VoiceItem
                key={voice.id}
                voice={voice}
                isPlaying={playingVoiceId === voice.id}
                isFavourite={favouriteCodes.includes(voice.code)}
                onPlay={() => handlePlay(voice)}
                onFavourite={() => handleFavourite(voice)}
              />
            ))}
      </div>
    </StyledTeachingVoices>
  );
};

export default TeachingVoices;
